import { BehaviorObservationSurvey, ReportData as Report, ReportTemplate } from '@/types/report';
import { apiClient } from './client';

interface ReportDefaultSection {
    section_type: string;
    content: string;
    updated_at: string;
}

interface BehaviorObservationsResponse {
    data: {
        report_id: string;
        payload: BehaviorObservationSurvey;
        updated_at: string;
    } | null;
}

interface ReportUsageResponse {
    limit: number;
    used: number;
    remaining: number;
    renews_at: string | null;
}

export interface ReportSummaryResponse {
    total: number;
    in_progress: number;
    completed: number;
    this_month: number;
}

interface ReportsPagination {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasMore: boolean;
}

interface ReportsPaginatedResponse {
    data: Report[];
    pagination: ReportsPagination;
}

interface ReportsListParams {
    ordering?: string;
    page?: number;
    pageSize?: number;
}

/**
 * Drops authoring-only block state on the way out. `awaiting_category` marks a
 * divider that was dropped before its category was picked — it means nothing
 * once the request leaves the tab, and the API's zod schema would strip it
 * silently anyway. Doing it here keeps every save path covered by one rule.
 */
const stripUiOnlyFields = <T extends Partial<Report>>(data: T): T => {
    const blocks = data.tests?.blocks;
    if (!blocks?.some((block) => block.block_type === 'divider' && block.awaiting_category)) {
        return data;
    }

    return {
        ...data,
        tests: {
            ...data.tests,
            blocks: blocks.map((block) =>
                block.block_type === 'divider' && block.awaiting_category
                    ? { ...block, awaiting_category: undefined }
                    : block,
            ),
        },
    };
};

type ReportUpdatePayload = Partial<Report> & {
    expected_content_version?: number;
    expected_updated_at?: string;
    applied_consolidation_ids?: string[];
};

export type ClinicalHistoryStatus = 'not_started' | 'queued' | 'building' | 'ready' | 'failed';

interface ClinicalHistoryProcessingState {
    status: ClinicalHistoryStatus;
    ready: boolean;
    trace_id: string | null;
    updated_at: string | null;
}

interface ClinicalHistoryStatusResponse {
    data: {
        clinical_history: ClinicalHistoryProcessingState;
    };
}

interface ReportTemplatesResponse {
    data: {
        catalog_templates: ReportTemplate[];
        user_templates: ReportTemplate[];
    };
}

interface ReportTemplateMetadataPayload {
    name?: string;
    description?: string;
}

const normalizeTemplate = (template: ReportTemplate): ReportTemplate => ({
    ...template,
    source: template.source || 'user',
    read_only: template.read_only ?? false,
    can_delete: template.can_delete ?? true,
});

export const reportsApi = {
    getUsage: async (): Promise<ReportUsageResponse> => {
        const response = await apiClient.get<ReportUsageResponse>('/reports/usage');
        return response as unknown as ReportUsageResponse;
    },
    getDefaults: async (): Promise<ReportDefaultSection[]> => {
        const response = await apiClient.get<{ data: ReportDefaultSection[] }>('/reports/defaults');
        return (response as unknown as { data: ReportDefaultSection[] }).data;
    },

    getTemplates: async (): Promise<ReportTemplate[]> => {
        const response = await apiClient.get<ReportTemplatesResponse>('/reports/templates/');
        const { data } = response as unknown as ReportTemplatesResponse;

        return [
            ...(data.user_templates || []).map(normalizeTemplate),
            ...(data.catalog_templates || []).map(normalizeTemplate),
        ];
    },

    getTemplate: async (templateId: string): Promise<ReportTemplate> => {
        const response = await apiClient.get<{ data: ReportTemplate }>(
            `/reports/templates/${templateId}`,
        );
        const { data } = response as unknown as { data: ReportTemplate };

        return normalizeTemplate(data);
    },

    list: async (params: ReportsListParams = {}): Promise<ReportsPaginatedResponse> => {
        const { ordering, page = 1, pageSize = 20 } = params;

        const response = await apiClient.get<ReportsPaginatedResponse>('/reports/', {
            params: {
                ordering,
                page,
                pageSize,
            },
        });

        return response as unknown as ReportsPaginatedResponse;
    },

    listAll: async (params: Omit<ReportsListParams, 'page'> = {}): Promise<Report[]> => {
        const pageSize = params.pageSize ?? 100;
        let page = 1;
        let hasMore = true;
        const reports: Report[] = [];

        while (hasMore) {
            const response = await reportsApi.list({
                ...params,
                page,
                pageSize,
            });

            reports.push(...response.data);
            hasMore = response.pagination.hasMore;
            page += 1;
        }

        return reports;
    },

    summary: async (): Promise<ReportSummaryResponse> => {
        const response = await apiClient.get<ReportSummaryResponse>('/reports/summary');
        return response as unknown as ReportSummaryResponse;
    },

    getById: async (id: string): Promise<Report> => {
        const response = await apiClient.get<{ data: Report }>(`/reports/${id}`);
        return (response as unknown as { data: Report }).data;
    },

    getBehaviorObservations: async (id: string): Promise<BehaviorObservationsResponse> => {
        const response = await apiClient.get<BehaviorObservationsResponse>(
            `/reports/${id}/behavior-observations`,
        );
        return response as unknown as BehaviorObservationsResponse;
    },

    getClinicalHistoryStatus: async (id: string): Promise<ClinicalHistoryStatusResponse> => {
        const response = await apiClient.get<ClinicalHistoryStatusResponse>(
            `/reports/${id}/anamnesis/status`,
        );
        return response as unknown as ClinicalHistoryStatusResponse;
    },

    create: async (data: Partial<Report>): Promise<Report> => {
        const response = await apiClient.post<{ data: Report }>(
            '/reports/',
            stripUiOnlyFields(data),
        );
        return (response as unknown as { data: Report }).data;
    },

    update: async (id: string, data: ReportUpdatePayload): Promise<Report> => {
        const response = await apiClient.put<{ data: Report }>(
            `/reports/${id}`,
            stripUiOnlyFields(data),
        );
        return (response as unknown as { data: Report }).data;
    },

    updateDefault: async (section_type: string, content: string): Promise<void> => {
        await apiClient.put(`/reports/defaults/${section_type}`, { content });
    },

    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/reports/${id}`);
    },

    export: async (id: string): Promise<Blob> => {
        const response = await apiClient.get<Blob>(`/reports/${id}/export`, {
            responseType: 'blob',
        });

        return response as unknown as Blob;
    },

    conclude: async (id: string): Promise<Report> => {
        const response = await apiClient.post<{ data: Report }>(`/reports/${id}/conclude`);
        return (response as unknown as { data: Report }).data;
    },

    saveTemplate: async (data: Partial<Report>): Promise<void> => {
        await apiClient.post('/reports/templates/', data);
    },

    updateTemplateMetadata: async (
        id: string,
        data: ReportTemplateMetadataPayload,
    ): Promise<ReportTemplate> => {
        const response = await apiClient.patch<{ data: ReportTemplate }>(
            `/reports/templates/${id}`,
            data,
        );
        const { data: template } = response as unknown as { data: ReportTemplate };

        return normalizeTemplate(template);
    },

    deleteTemplate: async (id: string): Promise<void> => {
        await apiClient.delete(`/reports/templates/${id}`);
    },
};
